import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import Section from '../components/Section';
import { FaCalendar, FaClock, FaVideo, FaPhone, FaCheck } from 'react-icons/fa';

const CustomSection = styled(Section)`
  padding-top: 1.5rem !important;
  padding-bottom: 2rem !important;
  
  & > div > div {
    margin-bottom: 0.75rem;
  }
  
  h2 {
    font-size: 1.75rem;
    margin-bottom: 0.25rem;
  }
  
  p {
    margin-bottom: 0.75rem;
    font-size: 0.9rem;
  }
`;

const ReservaContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const ReservaIntro = styled.p`
  text-align: center;
  font-size: 0.9rem;
  color: var(--gray);
  max-width: 700px;
  margin: 0 auto 0.75rem;
`;

const ReservaContent = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  
  @media (min-width: 992px) {
    grid-template-columns: 1fr 3fr;
    align-items: start;
  }
`;

const ReservaInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const SelectedPlan = styled.div`
  background: linear-gradient(135deg, #e9f5ff 0%, #dcebff 100%);
  border-radius: 0.75rem;
  padding: 0.75rem;
  text-align: center;
  margin-bottom: 0.75rem;
  
  h3 {
    font-size: 1rem;
    color: var(--blue);
    margin-bottom: 0.25rem;
  }
  
  p {
    font-size: 0.8rem;
    color: var(--gray);
    margin-bottom: 0.25rem;
  }
  
  .price {
    font-size: 1rem;
    font-weight: 700;
    color: var(--gray-dark);
  }
`;

const ReservaBenefits = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const BenefitCard = styled.div`
  background-color: white;
  border-radius: 0.75rem;
  padding: 0.75rem;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  text-align: left;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  }
`;

const IconContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e9f5ff 0%, #dcebff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  flex-shrink: 0;
  
  svg {
    font-size: 1rem;
    color: var(--blue);
  }
`;

const BenefitContent = styled.div``;

const BenefitTitle = styled.h3`
  font-size: 0.9rem;
  margin-bottom: 0.2rem;
  color: var(--gray-dark);
`;

const BenefitText = styled.p`
  font-size: 0.8rem;
  color: var(--gray);
  line-height: 1.2;
  margin: 0;
`;

const CalendarContainer = styled.div`
  background-color: white;
  border-radius: 0.75rem;
  padding: 0.5rem;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
  height: 750px;
  overflow: hidden;
  
  iframe {
    width: 100%;
    height: 740px;
    border: none;
  }
`;

// Tipo para definir la información de los planes
type PlanInfo = {
  [key: string]: {
    name: string;
    description: string;
    price: number;
  }
};

const ReservaPage = () => {
  const location = useLocation();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [iframeHeight, setIframeHeight] = useState(740);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  
  // Información de los planes disponibles
  const planesInfo: PlanInfo = {
    basico: {
      name: "Web Básica",
      description: "Ideal para profesionales o pequeños negocios que quieren una presencia online básica.",
      price: 200
    },
    estandar: {
      name: "Web Estándar",
      description: "Para empresas que buscan una web más completa con mejores funcionalidades.",
      price: 300
    },
    avanzado: {
      name: "Web Avanzada",
      description: "Solución completa para empresas que necesitan una web profesional con todas las funcionalidades.",
      price: 400
    }
  };
  
  useEffect(() => {
    // Capturar el parámetro 'plan' de la URL
    const searchParams = new URLSearchParams(location.search);
    const plan = searchParams.get('plan');
    if (plan) {
      setSelectedPlan(plan);
    }
    
    // Ajustar altura del iframe a la ventana
    const adjustIframeHeight = () => {
      const windowHeight = window.innerHeight;
      const headerHeight = 200; // Estimación del espacio que ocupa el header
      const titleHeight = 100; // Estimación del espacio que ocupa el título y subtítulo
      const availableHeight = windowHeight - headerHeight - titleHeight - 40; // 40px de margen adicional
      
      setIframeHeight(Math.max(740, availableHeight));
    };
    
    adjustIframeHeight();
    window.addEventListener('resize', adjustIframeHeight);
    
    return () => {
      window.removeEventListener('resize', adjustIframeHeight);
    };
  }, [location]);
  
  return (
    <Layout>
      <SEO 
        title="Reserva tu Cita | Web a Bajo Coste" 
        description="Agenda una consulta personalizada para discutir tu proyecto de página web. Nuestro equipo te ayudará a definir la mejor solución para tu negocio." 
        keywords="reservar cita, consulta web, asesoramiento diseño web, presupuesto personalizado"
        canonical="https://webabajocoste.es/reserva"
      />
      
      <CustomSection
        title="Reserva una consulta personalizada"
        subtitle="Elige el día y hora que mejor te convenga"
        background="light"
        centered
      >
        <ReservaContainer>
          <ReservaIntro>
            Reserva una cita de 30 minutos para hablar de tu proyecto web y recibir asesoramiento personalizado.
          </ReservaIntro>
          
          <ReservaContent>
            <ReservaInfo>
              {selectedPlan && planesInfo[selectedPlan] && (
                <SelectedPlan>
                  <h3>Plan: {planesInfo[selectedPlan].name}</h3>
                  <p>{planesInfo[selectedPlan].description}</p>
                  <div className="price">
                    <FaCheck style={{ color: 'var(--green)', marginRight: '0.5rem', fontSize: '0.8rem' }} />
                    {planesInfo[selectedPlan].price}€ + 20€/mes
                  </div>
                </SelectedPlan>
              )}
              
              <ReservaBenefits>
                <BenefitCard>
                  <IconContainer>
                    <FaCalendar />
                  </IconContainer>
                  <BenefitContent>
                    <BenefitTitle>Elige tu día</BenefitTitle>
                    <BenefitText>L-V, horario flexible</BenefitText>
                  </BenefitContent>
                </BenefitCard>
                
                <BenefitCard>
                  <IconContainer>
                    <FaClock />
                  </IconContainer>
                  <BenefitContent>
                    <BenefitTitle>30 minutos</BenefitTitle>
                    <BenefitText>Consulta personalizada</BenefitText>
                  </BenefitContent>
                </BenefitCard>
                
                <BenefitCard>
                  <IconContainer>
                    <FaVideo />
                  </IconContainer>
                  <BenefitContent>
                    <BenefitTitle>Online</BenefitTitle>
                    <BenefitText>Por videollamada</BenefitText>
                  </BenefitContent>
                </BenefitCard>
                
                <BenefitCard>
                  <IconContainer>
                    <FaPhone />
                  </IconContainer>
                  <BenefitContent>
                    <BenefitTitle>Presupuesto</BenefitTitle>
                    <BenefitText>Detallado y preciso</BenefitText>
                  </BenefitContent>
                </BenefitCard>
              </ReservaBenefits>
            </ReservaInfo>
            
            <CalendarContainer style={{ height: `${iframeHeight}px` }}>
              {/* Google Calendar Appointment Scheduling */}
              <iframe 
                ref={iframeRef}
                src={`https://calendar.google.com/calendar/appointments/schedules/AcZssZ0iDeolO_kdyCYTcu3vHiWbM3DCnTlAdYz28njB92f1Mcm0XR1pBSkIz0TaEFeYjE1S-uiCwFmJ?gv=true${selectedPlan ? '&plan=' + selectedPlan : ''}`} 
                title="Reserva tu cita con Web a Bajo Coste"
                frameBorder="0"
                style={{ height: `${iframeHeight - 10}px` }}
              ></iframe>
            </CalendarContainer>
          </ReservaContent>
        </ReservaContainer>
      </CustomSection>
    </Layout>
  );
};

export default ReservaPage; 